import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { applyBrandColor, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

const digits = (v) => (v || "").replace(/\D/g, "");
const formatPhone = (v) => {
  const d = digits(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export default function Signup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", shop_name: "", whatsapp: "", email: "", password: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    applyBrandColor(DEFAULT_BRAND_COLOR);
    document.title = "AutoOficina — Teste grátis";
    return () => { document.title = "AutoOficina"; };
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      if (user.role === "super_admin") navigate("/admin", { replace: true });
      else if (user.role === "owner") navigate("/owner", { replace: true });
      else navigate("/cliente", { replace: true });
    }
  }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/auth/signup", {
        name: form.name.trim(), shop_name: form.shop_name.trim(),
        whatsapp: digits(form.whatsapp), email: form.email.trim(),
        password: form.password, website: form.website,
      });
      toast.success("Oficina criada! Seu teste grátis de 14 dias começou.");
      await refresh();
      navigate("/owner", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <img src="/brand/autooficina-logo-white.png" alt="AutoOficina" data-testid="brand-logo"
               className="mx-auto h-20 sm:h-24 w-auto drop-shadow-[0_0_30px_rgba(74,222,128,0.35)] hidden dark:block" />
          <img src="/brand/autooficina-logo.png" alt="AutoOficina"
               className="mx-auto h-20 sm:h-24 w-auto drop-shadow-[0_0_30px_rgba(74,222,128,0.35)] dark:hidden" />
          <p className="font-mono-caps text-xs text-zinc-500 mt-2">Rede de oficinas conectada</p>
        </div>

        <div data-testid="signup-card" className="rounded-2xl border border-card2-border bg-card2 backdrop-blur-md p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="h-4 w-4" style={{ color: "var(--c-brand)" }} />
            <span className="text-xs font-mono-caps text-zinc-400">Teste grátis 14 dias</span>
          </div>
          <h2 className="font-display text-2xl font-bold uppercase text-zinc-100 mb-2">Criar oficina</h2>
          <p className="text-xs text-zinc-400 mb-6">Depois do teste, você escolhe o plano. Nada é apagado.</p>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form" autoComplete="off">
            <div>
              <Label htmlFor="su-name" className="text-xs font-mono-caps text-zinc-400">Seu nome</Label>
              <Input id="su-name" data-testid="signup-name" required value={form.name} onChange={set("name")} autoComplete="name"
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="João da Silva" />
            </div>
            <div>
              <Label htmlFor="su-shop" className="text-xs font-mono-caps text-zinc-400">Nome da oficina</Label>
              <Input id="su-shop" data-testid="signup-shop" required value={form.shop_name} onChange={set("shop_name")} autoComplete="organization"
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="Oficina do João" />
            </div>
            <div>
              <Label htmlFor="su-wa" className="text-xs font-mono-caps text-zinc-400">WhatsApp</Label>
              <Input id="su-wa" data-testid="signup-whatsapp" required inputMode="tel" autoComplete="tel"
                     value={formatPhone(form.whatsapp)} onChange={(e) => setForm((f) => ({ ...f, whatsapp: digits(e.target.value) }))}
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="(31) 99999-9999" />
            </div>
            <div>
              <Label htmlFor="su-email" className="text-xs font-mono-caps text-zinc-400">E-mail</Label>
              <Input id="su-email" data-testid="signup-email" type="email" required autoComplete="email"
                     value={form.email} onChange={set("email")}
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="voce@oficina.com" />
            </div>
            <div>
              <Label htmlFor="su-pw" className="text-xs font-mono-caps text-zinc-400">Senha</Label>
              <Input id="su-pw" data-testid="signup-password" type="password" required autoComplete="new-password"
                     value={form.password} onChange={set("password")}
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="••••••••" />
              <p className="text-[11px] text-zinc-500 mt-1">Mínimo 8 caracteres, com letra e número.</p>
            </div>
            {/* Honeypot anti-robô: campo invisível */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <label htmlFor="su-website">Website</label>
              <input id="su-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>
            {error && (<div data-testid="signup-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-md px-3 py-2">{error}</div>)}
            <Button data-testid="signup-submit-btn" type="submit" disabled={loading}
                    style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}
                    className="w-full h-12 rounded-2xl font-bold uppercase tracking-wider shadow-[0_8px_28px_-10px_rgba(var(--c-brand-rgb)/0.55)] hover:brightness-110">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Sparkles className="h-4 w-4 mr-2" /> Começar teste grátis de 14 dias</>)}
            </Button>
          </form>
          <p className="text-xs text-zinc-500 mt-6 text-center">
            Já tem conta? <Link to="/login" data-testid="signup-to-login" className="hover:underline" style={{ color: "var(--c-brand-neon)" }}>Entrar</Link>
          </p>
        </div>
        <p data-testid="legal-links" className="mt-6 text-[11px] text-zinc-600 text-center">
          <Link to="/privacidade" className="hover:text-zinc-400 transition-colors">Privacidade</Link>
          <span className="mx-2">·</span>
          <Link to="/termos" className="hover:text-zinc-400 transition-colors">Termos de Serviço</Link>
        </p>
      </div>
    </div>
  );
}
